import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useTheme, alpha } from '@mui/material/styles';

/**
 * Delta-Chip (Charts 2.0): vorzeichenbehaftete Veränderung gegen eine
 * benannte Vorperiode. Grün/Rot folgt der Richtung × „ist rauf gut?" —
 * bei delta === 0 neutral.
 *   delta: number · deltaLabel: z. B. „30 Tage" · goodIsUp: default true
 */
export function DeltaChip({ delta, deltaLabel, goodIsUp = true }) {
  const theme = useTheme();
  if (delta === null || delta === undefined) return null;
  const up = delta > 0;
  const neutral = delta === 0;
  const good = neutral ? null : (up === goodIsUp);
  const color = neutral ? theme.palette.text.secondary
    : good ? theme.palette.success.main : theme.palette.error.main;
  const sign = up ? '+' : '';
  return (
    <Box component="span" sx={{
      display: 'inline-flex', alignItems: 'center', gap: 0.4, flex: 'none',
      fontSize: '0.72rem', fontWeight: 650, borderRadius: '6px', px: 0.75, py: 0.2,
      color, bgcolor: alpha(color, 0.12), fontVariantNumeric: 'tabular-nums',
    }}>
      {neutral ? '±' : up ? '▲' : '▼'} {sign}{delta}{deltaLabel ? ` · ${deltaLabel}` : ''}
    </Box>
  );
}

/**
 * Sparkline (Charts 2.0): 12-Punkte-Miniverlauf — 2px-Linie, 10%-Flächen-
 * Wash, betonter Endpunkt mit Ring in der Flächenfarbe der Kachel.
 *   points: number[] (chronologisch)
 */
export function Sparkline({ points, color, height = 40 }) {
  const theme = useTheme();
  const c = color || theme.palette.tokens.ramp[500];
  if (!points || points.length < 2) return null;
  const W = 220, H = 40, PAD = 5;
  const max = Math.max(...points, 1);
  const min = Math.min(...points, 0);
  const span = max - min || 1;
  const px = (i) => PAD + (i / (points.length - 1)) * (W - 2 * PAD);
  const py = (v) => H - PAD - ((v - min) / span) * (H - 2 * PAD);
  const line = points.map((v, i) => `${i === 0 ? 'M' : 'L'}${px(i).toFixed(1)},${py(v).toFixed(1)}`).join(' ');
  const area = `${line} L${px(points.length - 1).toFixed(1)},${H} L${px(0).toFixed(1)},${H} Z`;
  return (
    <Box component="svg" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none"
      sx={{ width: '100%', height, display: 'block', mt: 'auto' }} aria-hidden="true">
      <path d={area} fill={c} opacity={0.1} />
      <path d={line} fill="none" stroke={c} strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" vectorEffect="non-scaling-stroke" />
      <circle cx={px(points.length - 1)} cy={py(points[points.length - 1])} r={4}
        fill={c} stroke={theme.palette.background.paper} strokeWidth={2} />
    </Box>
  );
}

/**
 * Stat-Tile (Charts 2.0): der KPI-Vertrag — Label · Wert · Delta gegen
 * benannte Vorperiode · optionaler Sparkline-Trend. Wert trägt Tinte,
 * nie die Datenfarbe (Identität kommt vom Mark, nicht vom Text).
 *
 *   label      – Kachel-Beschriftung
 *   value      – der aktuelle Wert (fertig formatiert)
 *   sub        – kleine Unterzeile (z. B. „gewonnene Leads")
 *   delta      – Zahl (optional) → DeltaChip; deltaLabel benennt die Periode
 *   goodIsUp   – false, wenn „weniger ist besser"
 *   spark      – number[] (optional) → Sparkline füllt den Kachelboden
 *   sparkColor – Farbe des Sparks (default: Indigo-Ramp 500)
 */
export default function StatTile({ label, value, sub, delta, deltaLabel, goodIsUp = true, spark, sparkColor, onClick }) {
  return (
    <Box onClick={onClick} sx={{
      height: '100%', display: 'flex', flexDirection: 'column', minWidth: 0,
      cursor: onClick ? 'pointer' : 'default',
    }}>
      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 550, fontSize: '0.82rem', lineHeight: 1.5 }} noWrap>
        {label}
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, minWidth: 0 }}>
        <Typography sx={{ fontSize: 'clamp(1.4rem, 2.2vw, 1.9rem)', fontWeight: 700, mt: 0.25, color: 'text.primary', fontVariantNumeric: 'tabular-nums', lineHeight: 1.15 }}>
          {value ?? '—'}
        </Typography>
        <DeltaChip delta={delta} deltaLabel={deltaLabel} goodIsUp={goodIsUp} />
      </Box>
      {sub && <Typography variant="caption" color="text.secondary" noWrap>{sub}</Typography>}
      {spark && <Sparkline points={spark} color={sparkColor} />}
    </Box>
  );
}
